import classNames from 'classnames';
import { CSSProperties, ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  DivisionChange,
  So5LeaderboardType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { Ambassadors } from '@sorare/core/src/components/landing/Ambassadors';
import { CommunityBlock } from '@sorare/core/src/components/landing/CommunityBlock';
import { Compete } from '@sorare/core/src/components/landing/Compete';
import type { CompeteAssets } from '@sorare/core/src/components/landing/Compete';
import { Footer } from '@sorare/core/src/components/landing/Footer';
import { HeroVideo } from '@sorare/core/src/components/landing/HeroVideo';
import { Leagues } from '@sorare/core/src/components/landing/Leagues';
import type { League } from '@sorare/core/src/components/landing/Leagues';
import { Scout } from '@sorare/core/src/components/landing/Scout';
import type { ScoutAssets } from '@sorare/core/src/components/landing/Scout';
import { Win } from '@sorare/core/src/components/landing/Win';
import type { WinAssets } from '@sorare/core/src/components/landing/Win';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { LeagueImage } from '@sorare/gameplay/src/components/league/LeagueImage';
import { DumbStatus } from '@sorare/gameplay/src/components/managerTeam/Status/DumbStatus';

import { FAQ } from 'components/landing/shared/FAQ';

import { Banner } from './Banner';
import { Chart } from './Chart';
import footerBackgroundAvif from './assets/footer/background.avif';
import footerBackground from './assets/footer/background.jpg';
import winBackgroundAvif from './assets/win/background.avif';
import winBackground from './assets/win/background.jpg';
import jerseyAvif from './assets/win/jersey.avif';
import jersey from './assets/win/jersey.png';
import ticketsAvif from './assets/win/tickets.avif';
import tickets from './assets/win/tickets.png';
import vipAvif from './assets/win/vip.avif';
import vip from './assets/win/vip.png';

const Promotion = styled(Horizontal)`
  filter: drop-shadow(0px 0px 15px rgba(34, 255, 96, 0.7));
  font-size: 2em;
  p {
    text-shadow: 0px 0px 36px #22ff60;
  }
`;
const leaguesAssets = [
  {
    id: 'u23',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-under_twenty_one/picture.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-under_twenty_one/header.svg',
  },
  {
    id: 'seasonal-spain',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/header.svg',
  },
  {
    id: 'seasonal-england',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-england/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-england/header.svg',
  },
  {
    id: 'seasonal-germany',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/header.svg',
  },
  {
    id: 'seasonal-champions',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-champions/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-champions/header.svg',
  },
];

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--quadruple-unit);
  overflow: hidden;
  position: relative;
  padding-bottom: var(--double-unit);
  @media ${tabletAndAbove} {
    gap: calc(8 * var(--unit));
  }
`;
const Hero = styled.div`
  &.showOtherLeagues {
    @media ${tabletAndAbove} {
      margin-bottom: 100px;
    }
  }
`;
const Players = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex: 1;
  padding-bottom: calc(8 * var(--unit));
  background-image: var(--players);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center top -5%;
  min-width: 100%;
  max-width: 100%;
  @media ${laptopAndAbove} {
    height: 100%;
    background-size: 900px;
    background-position: right -20% top;
  }
`;
const ChartWrapper = styled.div`
  transform: scale(0.75);
  transform-origin: bottom;
  margin-top: calc(10 * var(--unit));
  @media ${laptopAndAbove} {
    margin: 0 15% 0 auto;
  }
`;

const ImgWrapper = styled.div`
  padding: 0 var(--unit);
`;

type Props = {
  showFAQ?: boolean;
  leagues?: League[];
  starPlayerSlug: string;
  assets: {
    scout: ScoutAssets;
    compete: CompeteAssets & { cards: { avif: string; pictureUrl: string } };
    win: Omit<WinAssets, 'background'>;
  };
  leaderboards: So5LeaderboardType[];
  league?: ReactNode;
  variant:
    | 'Football'
    | 'Bundesliga_landing'
    | 'LaLigaEA_landing'
    | 'EPL_launch'
    | 'SerieA_landing';
};

export const EarlyAccess = ({
  showFAQ,
  leagues,
  starPlayerSlug,
  assets,
  leaderboards,
  league,
  variant,
}: Props) => {
  const track = useEvents();
  const isLoggedIn = useIsLoggedIn();

  useEffect(() => {
    if (!isLoggedIn) {
      track('View Homepage Disconnected', {
        landing_variant: variant,
      });
    }
  }, [isLoggedIn, track, variant]);

  return (
    <Root>
      <Hero className={classNames({ showOtherLeagues: !!leagues })}>
        <HeroVideo
          league={league}
          title={
            <FormattedMessage
              id="landing.EarlyAccess.title"
              defaultMessage="Fantasy Football. Reinvented"
            />
          }
          subtitle={
            <FormattedMessage
              id="landing.EarlyAccess.description3"
              defaultMessage="Think you know ball? Create your fantasy club now and compete for VIP tickets, signed jerseys and more."
            />
          }
        />
        {leagues && <Leagues leagues={leagues} />}
      </Hero>
      <Banner />
      <Scout
        assets={assets.scout}
        title={
          <FormattedMessage
            id="landing.EarlyAccess.Scout.title"
            defaultMessage="Scout"
          />
        }
        description={
          <FormattedMessage
            id="landing.EarlyAccess.Scout.description"
            defaultMessage="Build your club from the ground up. Use your knowledge to buy, trade and sell players on our marketplace."
          />
        }
        ctaLabel={
          <FormattedMessage
            id="landing.EarlyAccess.Scout.cta"
            defaultMessage="Create your club"
          />
        }
        players={
          <Players
            style={
              {
                '--players': `image-set(url(${assets.scout.players.avif}) type('image/avif'), url(${assets.scout.players.pictureUrl}) type('image/jpeg'))`,
              } as CSSProperties
            }
          >
            <ChartWrapper>
              <Chart slug={starPlayerSlug} />
            </ChartWrapper>
          </Players>
        }
      >
        {leaguesAssets.map(({ id, ...assetProps }) => (
          <LeagueImage key={id} {...assetProps} />
        ))}
      </Scout>
      <Compete
        assets={assets.compete}
        title={
          <FormattedMessage
            id="landing.EarlyAccess.Compete.title"
            defaultMessage="Compete"
          />
        }
        description={
          <FormattedMessage
            id="landing.EarlyAccess.Compete.description"
            defaultMessage="Set 5-player lineups and compete in fantasy competitions across football's top competitions."
          />
        }
        img={
          <ImgWrapper>
            <picture>
              <source srcSet={assets.compete.cards.avif} type="image/avif" />
              <Img
                src={assets.compete.cards.pictureUrl}
                fetchpriority="low"
                width="543"
                height="661"
              />
            </picture>
          </ImgWrapper>
        }
        promotion={
          <Promotion>
            <DumbStatus divisionChange={DivisionChange.PROMOTED} />
            <BodyL bold as="p">
              <FormattedMessage
                id="landing.EarlyAccess.Compete.promotion"
                defaultMessage="Promotion"
              />
            </BodyL>
            <Img
              src="https://frontend-assets.sorare.com/football/manager_team/limited-1-in/icon.png"
              fetchpriority="low"
              width="64"
              height="64"
            />
          </Promotion>
        }
      />
      <Container>
        <Win
          assets={{
            ...assets.win,
            background: {
              avif: winBackgroundAvif,
              pictureUrl: winBackground,
            },
          }}
          title={
            <FormattedMessage
              id="landing.EarlyAccess.Win.title"
              defaultMessage="Win"
            />
          }
          description={
            <FormattedMessage
              id="landing.EarlyAccess.Win.description2"
              defaultMessage="Play for more than bragging rights. Compete for digital player cards, signed jerseys, VIP experiences and much more."
            />
          }
          ctaLabel={
            <FormattedMessage
              id="landing.EarlyAccess.Win.cta"
              defaultMessage="Compete now"
            />
          }
          rewards={[
            {
              id: 'vip',
              avif: vipAvif,
              pictureUrl: vip,
              width: 197,
              height: 248,
              label: (
                <FormattedMessage
                  id="landing.EarlyAccess.Win.vip"
                  defaultMessage="Vip Experience"
                />
              ),
            },
            {
              id: 'cards',
              avif: assets.win.cards.avif,
              pictureUrl: assets.win.cards.pictureUrl,
              width: 210,
              height: 193,
              label: (
                <FormattedMessage
                  id="landing.EarlyAccess.Win.cards"
                  defaultMessage="Playable Cards"
                />
              ),
            },
            {
              id: 'jersey',
              avif: jerseyAvif,
              pictureUrl: jersey,
              width: 161,
              height: 161,
              label: (
                <FormattedMessage
                  id="landing.EarlyAccess.Win.jersey"
                  defaultMessage="Signed Jersey"
                />
              ),
            },
            {
              id: 'tickets',
              avif: ticketsAvif,
              pictureUrl: tickets,
              width: 136,
              height: 168,
              label: (
                <FormattedMessage
                  id="landing.EarlyAccess.Win.tickets"
                  defaultMessage="Match Tickets"
                />
              ),
            },
          ]}
        />
      </Container>
      <CommunityBlock
        leaderboards={leaderboards.map(leaderboardType => ({
          sport: Sport.FOOTBALL,
          leaderboardType,
        }))}
      />
      <Container>
        <Ambassadors />
      </Container>
      <Footer
        title={
          <FormattedMessage
            id="landing.EarlyAccess.Footer.title"
            defaultMessage="Your new football home"
          />
        }
        assets={{ avif: footerBackgroundAvif, pictureUrl: footerBackground }}
      />
      {showFAQ && <FAQ />}
    </Root>
  );
};
